/** Webpack substitutes the bundle's CDN root for this at build time. */
declare const __webpack_public_path__: string;

/** The subset of PerformanceResourceTiming the summary reads. */
export interface ResourceEntry {
  name: string;
  duration: number;
  responseEnd: number;
  /** 0 for cross-origin resources unless the server sends Timing-Allow-Origin. */
  transferSize: number;
}

export interface ExtensionResourceSummary {
  /** Scripts and stylesheets fetched from the extension CDN origin. */
  count: number;
  /** Encoded bytes on the wire; 0 until the CDN sends Timing-Allow-Origin. */
  bytes: number;
  /** Sum of every resource's fetch duration, in ms. */
  totalMs: number;
  slowestMs: number;
  /** Path of the slowest resource, relative to the CDN origin. */
  slowest?: string;
  /** ms from navigation start until the last extension resource finished. */
  lastResponseEndMs: number;
}

function bundlePublicPath(): string | undefined {
  return typeof __webpack_public_path__ === "string"
    ? __webpack_public_path__
    : undefined;
}

/**
 * The origin every entry and chunk is served from, taken from the bundle's own
 * public path rather than guessed from the page's resources.
 */
export function extensionOrigin(
  publicPath: string | undefined = bundlePublicPath(),
): string | undefined {
  if (!publicPath) {
    return undefined;
  }
  try {
    return new URL(publicPath).origin;
  } catch {
    return undefined;
  }
}

export function summarizeExtensionResources(
  entries: readonly ResourceEntry[],
  origin: string,
): ExtensionResourceSummary {
  const summary: ExtensionResourceSummary = {
    count: 0,
    bytes: 0,
    totalMs: 0,
    slowestMs: 0,
    lastResponseEndMs: 0,
  };
  for (const entry of entries) {
    if (!entry.name.startsWith(`${origin}/`)) {
      continue;
    }
    summary.count += 1;
    summary.bytes += entry.transferSize;
    summary.totalMs += entry.duration;
    summary.lastResponseEndMs = Math.max(
      summary.lastResponseEndMs,
      entry.responseEnd,
    );
    if (entry.duration > summary.slowestMs) {
      summary.slowestMs = entry.duration;
      summary.slowest = entry.name.slice(origin.length);
    }
  }
  return summary;
}

function isResourceEntry(
  entry: PerformanceEntry,
): entry is ResourceEntry & PerformanceEntry {
  return entry.entryType === "resource" && "transferSize" in entry;
}

/** Never throws: a missing Performance API just leaves the field out. */
export function extensionResourceSummary():
  | ExtensionResourceSummary
  | undefined {
  try {
    const origin = extensionOrigin();
    if (!origin) {
      return undefined;
    }
    const entries = performance
      .getEntriesByType("resource")
      .filter(isResourceEntry);
    return summarizeExtensionResources(entries, origin);
  } catch {
    return undefined;
  }
}

export function visitorTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}
